/**
 * V17.523 (Phase 1, brief 2026-10-26-002): THE sidebar — one menu on every staff page
 * (design record 2026-10-25-050, Rules 1–11; build rules B7–B9). Replaces the nine sidebars.
 *
 * Desktop: 256 px expanded or the 80 px rail (LayoutContext). The rail shows Home + one icon per
 * visible section; a click or a 150 ms hover opens that section's flyout; the active section's
 * icon gets a filled blue pill.
 * Phone: a left Sheet, 320 px (max 85vw), opened by MobileHeader's menu button; rows ≥44 px,
 * a 16 px search that filters in place, pinned footer; tapping an item navigates and closes it
 * (B8); close by ×, scrim tap or swipe left (no edge swipe-to-open). The registry is bundled, so
 * opening the drawer makes no network request.
 */
import { useCallback, useMemo, useRef, useState, type TouchEvent } from 'react';
import { ChevronsLeft, ChevronsRight, Search, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { HOME } from '../../app/navigation';
import { useLanguage } from '../../contexts/LanguageContext';
import { useLayout } from '../../contexts/LayoutContext';
import { cn } from '@/lib/utils';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetTitle } from '../ui/sheet';
import { TooltipProvider } from '../ui/tooltip';
import { NAV_ICONS } from './icons';
import { searchItems, type VisibleSection } from './navModel';
import { NavSearchDialog, useSearchShortcut } from './NavSearchDialog';
import { NAV_STRINGS, type NavLang } from './navStrings';
import { SectionList } from './SectionList';
import { SidebarFooter } from './SidebarFooter';
import { SidebarRoleControl } from './SidebarRoleControl';
import { useNavMenu } from './useNavMenu';

export const EXPANDED_WIDTH = 256;
export const RAIL_WIDTH = 80;

function TopSlot({ lang, compact, onSwitched }: { lang: NavLang; compact?: boolean; onSwitched?: () => void }) {
  const s = NAV_STRINGS[lang];
  if (compact) return <SidebarRoleControl lang={lang} variant="rail" />;
  // Rule 9's reserved slot. V17.539 (2C): the brand line + the one role control (desktop and phone drawer).
  return (
    <div className={cn('border-b border-sidebar-border px-4 py-3', onSwitched && 'min-w-0 flex-1')} data-testid="workspace-slot">
      <p className="text-sm font-bold text-sidebar-strong">{s.brand}</p>
      <div className="mt-1">
        <SidebarRoleControl lang={lang} variant={onSwitched ? 'drawer' : 'expanded'} onSwitched={onSwitched} />
      </div>
    </div>
  );
}

function RailSection({ section, activePath, active, lang }: { section: VisibleSection; activePath: string | null; active: boolean; lang: NavLang }) {
  const [open, setOpen] = useState(false);
  const Icon = NAV_ICONS[section.section.icon];
  return (
    <HoverCard open={open} onOpenChange={setOpen} openDelay={150} closeDelay={150}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          aria-expanded={open}
          aria-label={section.section.label[lang]}
          data-section={section.id}
          onClick={() => setOpen((v) => !v)}
          className={cn(
            'flex h-11 w-11 items-center justify-center rounded-xl transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
            active ? 'bg-sidebar-primary text-sidebar-primary-foreground' : 'text-sidebar-icon hover:bg-sidebar-hover',
          )}
        >
          <Icon className="h-5 w-5" aria-hidden="true" />
        </button>
      </HoverCardTrigger>
      <HoverCardContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto font-nav" lang={lang}>
        <p className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-sidebar-muted">{section.section.label[lang]}</p>
        <ul className="space-y-0.5" data-testid={`flyout-${section.id}`}>
          {section.items.map((item) => {
            const isActive = activePath === item.path;
            return (
              <li key={item.id}>
                <Link
                  to={item.path}
                  onClick={() => setOpen(false)}
                  aria-current={isActive ? 'page' : undefined}
                  data-nav-path={item.path}
                  className={cn(
                    'block rounded-md px-2 py-1.5 text-sm leading-snug',
                    isActive ? 'bg-sidebar-active-bg font-medium text-sidebar-active-fg' : 'text-sidebar-text hover:bg-sidebar-hover',
                  )}
                >
                  <span className="line-clamp-2">{item.label[lang]}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </HoverCardContent>
    </HoverCard>
  );
}

export default function AppSidebar({ isDesktop }: { isDesktop: boolean }) {
  const menu = useNavMenu();
  const { language } = useLanguage();
  const lang: NavLang = language === 'en' ? 'en' : 'lo';
  const s = NAV_STRINGS[lang];
  const { isDesktopSidebarOpen, toggleDesktopSidebar, isMobileSidebarOpen, setMobileSidebarOpen } = useLayout();
  const [searchOpen, setSearchOpen] = useState(false);
  const openSearch = useCallback(() => setSearchOpen(true), []);
  useSearchShortcut(openSearch);

  // Phone drawer: a 16 px input that filters the list in place.
  const [filter, setFilter] = useState('');
  const filteredSections = useMemo(() => {
    if (!filter.trim()) return menu.sections;
    const hit = new Set(searchItems(filter, menu.items).map((i) => i.id));
    return menu.sections.map((sec) => ({ ...sec, items: sec.items.filter((i) => hit.has(i.id)) })).filter((sec) => sec.items.length);
  }, [filter, menu.sections, menu.items]);
  const filterOpen = useMemo(() => new Set(filteredSections.map((x) => x.id)), [filteredSections]);

  const closeDrawer = useCallback(() => setMobileSidebarOpen(false), [setMobileSidebarOpen]);
  const touchX = useRef<number | null>(null);
  const onTouchStart = (e: TouchEvent) => { touchX.current = e.touches[0]?.clientX ?? null; };
  const onTouchEnd = (e: TouchEvent) => {
    const start = touchX.current;
    touchX.current = null;
    const end = e.changedTouches[0]?.clientX;
    if (start !== null && end !== undefined && start - end > 60) closeDrawer(); // swipe left closes
  };

  const search = <NavSearchDialog open={searchOpen} onOpenChange={setSearchOpen} items={menu.items} lang={lang} />;

  if (!isDesktop) {
    return (
      <Sheet open={isMobileSidebarOpen} onOpenChange={(o) => { setMobileSidebarOpen(o); if (!o) setFilter(''); }}>
        <SheetContent className="font-nav" lang={lang} aria-label={s.nav} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <SheetTitle className="sr-only">{s.nav}</SheetTitle>
          <SheetDescription className="sr-only">{s.nav}</SheetDescription>
          <div className="flex items-start justify-between">
            <TopSlot lang={lang} onSwitched={closeDrawer} />
            <SheetClose
              className="m-2 flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-sidebar-icon hover:bg-sidebar-hover"
              aria-label={s.closeMenu}
            >
              <X className="h-5 w-5" />
            </SheetClose>
          </div>
          <div className="px-3 pt-3">
            <label className="flex min-h-[44px] items-center gap-2 rounded-lg border border-sidebar-border bg-sidebar-input px-3">
              <Search className="h-4 w-4 shrink-0 text-sidebar-muted" aria-hidden="true" />
              <input
                type="search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={s.searchPh}
                aria-label={s.search}
                className="w-full bg-transparent text-base text-sidebar-strong outline-none placeholder:text-sidebar-muted"
              />
            </label>
          </div>
          <nav className="flex-1 overflow-y-auto px-3 py-3" aria-label={s.nav}>
            {filter.trim() && filteredSections.length === 0 ? (
              <div className="px-2 py-6 text-center text-sm">
                <p className="font-medium text-sidebar-strong">{s.noRes(filter.trim())}</p>
                <p className="mt-1 text-sidebar-muted">{s.noResHint}</p>
                <p className="mt-1 text-sidebar-muted">{s.access}</p>
              </div>
            ) : (
              <SectionList
                homePath={menu.homePath}
                homeActive={menu.homeActive}
                sections={filteredSections}
                open={filter.trim() ? filterOpen : menu.open}
                onToggle={menu.toggleSection}
                activePath={menu.activePath}
                activeSection={menu.activeSection}
                lang={lang}
                touch
                onNavigate={closeDrawer}
              />
            )}
          </nav>
          <SidebarFooter touch />
        </SheetContent>
      </Sheet>
    );
  }

  if (!isDesktopSidebarOpen) {
    const HomeIcon = NAV_ICONS[HOME.icon];
    const { homeActive } = menu;
    return (
      <TooltipProvider>
        <aside
          className="flex h-[100dvh] w-20 shrink-0 flex-col border-r border-sidebar-border bg-sidebar font-nav text-sidebar-text"
          style={{ width: RAIL_WIDTH }}
          lang={lang}
          data-testid="app-sidebar"
          data-mode="rail"
        >
          <TopSlot lang={lang} compact />
          <nav className="flex flex-1 flex-col items-center gap-1 overflow-y-auto py-3" aria-label={s.nav}>
            <button type="button" onClick={openSearch} aria-label={s.search} title={`${s.search} (Ctrl K)`}
              className="mb-1 flex h-11 w-11 items-center justify-center rounded-xl text-sidebar-icon hover:bg-sidebar-hover">
              <Search className="h-5 w-5" />
            </button>
            <Link to={menu.homePath} aria-current={homeActive ? 'page' : undefined} aria-label={HOME.label[lang]} title={HOME.label[lang]}
              className={cn('flex h-11 w-11 items-center justify-center rounded-xl',
                homeActive ? 'bg-sidebar-primary text-sidebar-primary-foreground' : 'text-sidebar-icon hover:bg-sidebar-hover')}>
              <HomeIcon className="h-5 w-5" aria-hidden="true" />
            </Link>
            {menu.sections.map((sec) => (
              <RailSection key={sec.id} section={sec} activePath={menu.activePath} active={menu.activeSection === sec.id} lang={lang} />
            ))}
          </nav>
          <button type="button" onClick={toggleDesktopSidebar} aria-label={s.expand} title={s.expand} aria-expanded={false}
            className="mx-auto mb-1 flex h-9 w-9 items-center justify-center rounded-lg text-sidebar-icon hover:bg-sidebar-hover">
            <ChevronsRight className="h-5 w-5" />
          </button>
          <SidebarFooter rail />
          {search}
        </aside>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <aside
        className="flex h-[100dvh] w-64 shrink-0 flex-col border-r border-sidebar-border bg-sidebar font-nav text-sidebar-text"
        style={{ width: EXPANDED_WIDTH }}
        lang={lang}
        data-testid="app-sidebar"
        data-mode="expanded"
      >
        <TopSlot lang={lang} />
        <div className="px-3 pt-3">
          <button
            type="button"
            onClick={openSearch}
            className="flex min-h-[36px] w-full items-center gap-2 rounded-lg border border-sidebar-border bg-sidebar-input px-3 text-sm text-sidebar-muted hover:border-sidebar-accent"
          >
            <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="flex-1 text-left">{s.search}</span>
            <kbd className="rounded bg-sidebar-kbd px-1.5 text-[10px] text-sidebar-muted">Ctrl K</kbd>
          </button>
        </div>
        <div className="flex items-center justify-between px-4 pb-1 pt-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-sidebar-muted">{s.menu}</span>
          {menu.showToggleAll && (
            <button type="button" onClick={menu.toggleAll} className="rounded px-1 text-xs font-medium text-sidebar-accent hover:underline"
              data-testid="toggle-all">
              {menu.allOpen ? s.collapseAll : s.expandAll}
            </button>
          )}
        </div>
        <nav className="flex-1 overflow-y-auto px-3 pb-3" aria-label={s.nav}>
          <SectionList
            homePath={menu.homePath}
            homeActive={menu.homeActive}
            sections={menu.sections}
            open={menu.open}
            onToggle={menu.toggleSection}
            activePath={menu.activePath}
            activeSection={menu.activeSection}
            lang={lang}
          />
        </nav>
        <button type="button" onClick={toggleDesktopSidebar} aria-label={s.collapse} aria-expanded
          className="mx-3 mb-1 flex min-h-[36px] items-center gap-2 rounded-lg px-3 text-sm text-sidebar-muted hover:bg-sidebar-hover">
          <ChevronsLeft className="h-4 w-4" aria-hidden="true" />
          <span>{s.collapse}</span>
        </button>
        <SidebarFooter />
        {search}
      </aside>
    </TooltipProvider>
  );
}
